import { useCallback, useEffect } from 'preact/hooks';

import { useCurrentAsset } from './CurrentAssetProvider';
import { useNextAsset } from './NextAssetProvider/NextAssetProvider';
import { usePlayNext } from './usePlayNext';
import { useUpNextTracking } from './useUpNextTracking';
import { useAutoplayNextCountdown } from './useAutoplayNextCountdown';
import { useTpaasTracker } from './useTpaasTracker';

interface UseUpNextCountdownControlsProps {
  enabled: boolean;
  countdownInSeconds: number;
  onCancel: () => void;
}

export const useUpNextCountdownControls = ({
  enabled,
  countdownInSeconds,
  onCancel,
}: UseUpNextCountdownControlsProps) => {
  const { trackEngagementUIElement } = useTpaasTracker();
  const { playNext } = usePlayNext();
  const { nextAsset } = useNextAsset();
  const { asset } = useCurrentAsset();
  const {
    trackView,
    trackCancelClick,
    trackSkipClick,
    trackComplete,
    trackReplay,
  } = useUpNextTracking('countdown');

  const { remainingTime } = useAutoplayNextCountdown({
    enabled,
    countdownInSeconds,
    nextAsset: nextAsset || undefined,
    onCancelWhenHidden: onCancel,
    onBeforeAutoplayNext: trackComplete,
  });

  useEffect(() => {
    if (enabled) {
      trackView();
    }
  }, [enabled, trackView]);

  const handleCancelClick = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();

      if (enabled) {
        trackCancelClick();
      }

      onCancel();
    },
    [enabled, trackCancelClick, onCancel]
  );

  const handleReplayClick = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();

      if (enabled) {
        trackReplay();
        if (e?.currentTarget instanceof Element) {
          trackEngagementUIElement({
            elementName: 'Replay',
            elementText: e.currentTarget.textContent ?? undefined,
            elementType: e.currentTarget.tagName.toLowerCase(),
            name: 'Replay',
            playEntry: 'Replay',
          });
        }
      }

      playNext(asset!, {
        pulse: { playbackSource: 'replay' },
      });
    },
    [enabled, trackReplay, playNext, asset, trackEngagementUIElement]
  );

  const handleSkipClick = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();

      if (enabled) {
        trackSkipClick();
        if (e?.currentTarget instanceof Element) {
          trackEngagementUIElement({
            elementName: 'PlayNext Skip Countdown',
            elementText: e.currentTarget.textContent ?? undefined,
            elementType: e.currentTarget.tagName.toLowerCase(),
            name: 'PlayNext',
            playEntry: 'Next',
          });
        }
      }

      playNext(nextAsset!, {
        pulse: { playbackSource: 'autoplayNext-manual' },
      });
    },
    [enabled, trackSkipClick, playNext, nextAsset, trackEngagementUIElement]
  );

  const progressPercentage =
    ((countdownInSeconds - remainingTime) / countdownInSeconds) * 100;

  return {
    nextAsset,
    remainingTime,
    progressPercentage,
    handleCancelClick,
    handleReplayClick,
    handleSkipClick,
  };
};
